import _hasIn from 'lodash/hasIn';
import _isEmpty from 'lodash/isEmpty';
import _uniqWith from 'lodash/uniqWith';
import { startOfDay, isSameDay, isAfter, parseISO } from 'date-fns';
import { SCHEDULE_TYPES } from './ScheduleForm.helpers';
import messages from './ScheduleForm.messages';

const RECURRING_REQUIRED_FIELDS = ['startDate', 'classId', 'recurrence'];
const ONE_TIME_REQUIRED_FIELDS = ['startDate', 'classId'];

const isRecurrenceEmpty = recurrence => !Object.values(recurrence).some(selected => selected);

const validateRequiredValues = (requiredValues, values) => {
  const errors = requiredValues.reduce(
    (emptyValueErrors, requiredKey) =>
      !values[requiredKey]
        ? {
            ...emptyValueErrors,
            [requiredKey]: messages.validator.requiredField,
          }
        : emptyValueErrors,
    {}
  );

  const { recurrence } = values;

  if (recurrence && isRecurrenceEmpty(recurrence)) {
    errors.recurrence = messages.validator.requiredField;
  }

  return errors;
};

export const startAndDurationValidation = startAndDuration => {
  const startAndDurationErrors = [];

  if (startAndDuration) {
    startAndDuration.forEach((value, idx) => {
      const timeErrors = {};
      const durationInMinutesErrors = {};

      const { time, durationInMinutes } = value;

      if (!time) {
        timeErrors.time = messages.validator.requiredField;
        startAndDurationErrors[idx] = timeErrors;
      }

      if (time && startAndDuration.slice(0, idx).find(({ time: tm }) => time === tm)) {
        timeErrors.time = messages.validator.alreadySelectedTime;
        startAndDurationErrors[idx] = timeErrors;
      }

      if (_hasIn(value, 'durationInMinutes') && !durationInMinutes) {
        durationInMinutesErrors.durationInMinutes = messages.validator.requiredField;
        startAndDurationErrors[idx] = durationInMinutesErrors;
      }
    });
  }

  return { startAndDuration: startAndDurationErrors };
};

export const singleDayExceptionsValidator = (singleDayExceptions, startDate, endDate, isNoEndDate) => {
  if (_isEmpty(singleDayExceptions)) return {};
  const errors = {};

  const normalizedSingleDayExceptions = singleDayExceptions.map(day => startOfDay(parseISO(day)));

  if (normalizedSingleDayExceptions.length !== _uniqWith(normalizedSingleDayExceptions, isSameDay).length) {
    return { singleDayExceptions: { _error: messages.validator.singleDayExceptionDaysDuplicated } };
  }

  if (startDate) {
    if (normalizedSingleDayExceptions.some(day => isAfter(startOfDay(parseISO(startDate)), day))) {
      return { singleDayExceptions: { _error: messages.validator.singleDayExceptionDaysOutOfRange } };
    }

    if (endDate && !isNoEndDate) {
      if (normalizedSingleDayExceptions.some(day => isAfter(day, startOfDay(parseISO(endDate))))) {
        return { singleDayExceptions: { _error: messages.validator.singleDayExceptionDaysOutOfRange } };
      }
    }
  }

  return errors;
};

export const addScheduleSyncValidation = values => {
  const { scheduleType, isNoEndDate, startAndDuration, singleDayExceptions } = values;
  const recurringFields = isNoEndDate ? RECURRING_REQUIRED_FIELDS : ['recurrenceEndDate', ...RECURRING_REQUIRED_FIELDS];
  const requiredFields = scheduleType === SCHEDULE_TYPES.recurring ? recurringFields : ONE_TIME_REQUIRED_FIELDS;
  return {
    ...singleDayExceptionsValidator(singleDayExceptions, values.startDate, values.recurrenceEndDate, isNoEndDate),
    ...startAndDurationValidation(startAndDuration),
    ...validateRequiredValues(requiredFields, values),
  };
};

export const editScheduleSyncValidation = values => {
  const { scheduleType, isNoEndDate, singleDayExceptions } = values;
  const recurringFields = isNoEndDate ? RECURRING_REQUIRED_FIELDS : ['recurrenceEndDate', ...RECURRING_REQUIRED_FIELDS];
  const requiredFields = scheduleType === SCHEDULE_TYPES.recurring ? recurringFields : ONE_TIME_REQUIRED_FIELDS;
  return {
    ...singleDayExceptionsValidator(singleDayExceptions, values.startDate, values.recurrenceEndDate, isNoEndDate),
    ...validateRequiredValues([...requiredFields, 'durationInMinutes'], values),
  };
};
